/**
 * CityPro Delivery App — Mobile-first screen for delivery partners.
 * Only shows orders where I am the assigned delivery_partner.
 * One-tap "🏁 Delivered" → server splits AT to merchant + delivery + cascade.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../utils/api';

export default function CityProDelivery() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/citypro/delivery/my-orders');
      setOrders(r.data?.orders || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const complete = async (o) => {
    if (!window.confirm(`Mark "${o.service_name}" as delivered? Aap ka delivery payout credit ho jayega.`)) return;
    setBusyId(o.id);
    try {
      const r = await api.post(`/citypro/delivery/${o.id}/complete`);
      toast.success(`✅ Delivered! +${r.data.delivery_share_at || 0} AT credited`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusyId(null); }
  };

  const active = orders.filter((o) => ['accepted', 'in_progress'].includes(o.status));
  const done   = orders.filter((o) => o.status === 'completed').slice(0, 20);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-emerald-950/40 to-slate-950 text-white" data-testid="citypro-delivery-root">
      <div className="max-w-md mx-auto p-3 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-2xl font-black bg-gradient-to-r from-emerald-300 to-cyan-300 bg-clip-text text-transparent">🛵 Delivery App</div>
            <div className="text-[10px] opacity-70">CityPro · assigned to me</div>
          </div>
          <button onClick={load} data-testid="delivery-refresh"
                  className="px-3 py-1.5 rounded-full bg-slate-800 border border-slate-700 text-xs font-black">
            {loading ? '⏳' : '↻ Refresh'}
          </button>
        </div>

        <div className="rounded-2xl border-2 border-emerald-400/40 bg-emerald-500/10 p-3 text-center">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">ACTIVE DELIVERIES</div>
          <div className="text-4xl font-black text-emerald-200">{active.length}</div>
        </div>

        {active.length === 0 && (
          <div className="rounded-xl bg-slate-900 border border-slate-800 p-6 text-center text-xs opacity-70 space-y-3" data-testid="delivery-empty">
            <div>Koi active delivery nahi. Merchant assign karega toh yahaan aayega.</div>
            <a href="/citypro/delivery/register" data-testid="delivery-goto-register"
               className="inline-block px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-900 text-xs font-black">
              🚀 Register as Delivery Partner
            </a>
          </div>
        )}

        {active.map((o) => (
          <div key={o.id} className="rounded-2xl border-2 border-emerald-400/40 bg-slate-900 p-3 space-y-2" data-testid={`delivery-order-${o.id}`}>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <div className="text-sm font-black">{o.service_name}</div>
                <div className="text-[10px] opacity-70">Order #{o.id.slice(0, 8)} · @{o.customer_username}</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-black text-emerald-300">{o.delivery_fee_at || 0} AT</div>
                <div className="text-[9px] opacity-70">delivery payout</div>
              </div>
            </div>
            {o.pickup_addr && (
              <div className="rounded-lg bg-slate-800/60 p-2 text-xs">
                <div className="text-[10px] uppercase text-cyan-300 font-black">📍 Pickup / Delivery</div>
                <div>{o.pickup_addr}</div>
                {o.drop_addr && <div className="opacity-70 mt-0.5">→ {o.drop_addr}</div>}
              </div>
            )}
            {o.notes && <div className="text-[10px] italic opacity-70">📝 {o.notes}</div>}
            <div className="flex gap-1 text-[10px]">
              <span className="px-2 py-0.5 rounded bg-amber-500/25 text-amber-200 font-black">Total {o.amount_at} AT</span>
              {o.provider_username && <span className="px-2 py-0.5 rounded bg-slate-700 text-slate-300">🏪 @{o.provider_username}</span>}
            </div>
            <div className="flex gap-2 pt-1">
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`🛵 Delivery update · Order ${o.id.slice(0,8)}\nCustomer: @${o.customer_username}\nAddress: ${o.pickup_addr || ''}`)}`}
                target="_blank" rel="noreferrer"
                data-testid={`delivery-wa-${o.id}`}
                className="flex-1 py-2.5 rounded-xl bg-green-600 text-white text-sm font-black text-center">
                💬 Ping Customer
              </a>
              <button onClick={() => complete(o)} disabled={busyId === o.id}
                      data-testid={`delivery-complete-${o.id}`}
                      className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-900 text-sm font-black disabled:opacity-50">
                {busyId === o.id ? '⏳' : '🏁 Delivered'}
              </button>
            </div>
          </div>
        ))}

        {done.length > 0 && (
          <div className="rounded-2xl bg-slate-900 border border-slate-800 p-3 space-y-1">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black">Recently Delivered</div>
            {done.map((o) => (
              <div key={o.id} className="flex items-center gap-2 text-xs py-1 border-b border-slate-800 last:border-0" data-testid={`delivery-done-${o.id}`}>
                <span className="text-emerald-300">✓</span>
                <span className="flex-1 truncate">{o.service_name} · @{o.customer_username}</span>
                <span className="text-emerald-300 font-black">+{o.delivery_share_at || 0} AT</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
